import { useState } from "react";
import { FONTS, GRAD, PINK, CREAM, CARD_BORDER } from "../data/affirmations";
import { auth, googleProvider } from "../firebase";
import {
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";

interface AuthScreenProps {
  onAuth: (uid: string, name: string) => void;
}

export function AuthScreen({ onAuth }: AuthScreenProps) {
  const [mode, setMode] = useState<"login" | "signup">("signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      onAuth(user.uid, user.displayName || user.email || "Girl");
    } catch (e: any) {
      setError("Google sign in failed. Try again 💕");
    }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (mode === "signup" && !name.trim()) { setError("Enter your name 💕"); return; }
    if (!email.includes("@")) { setError("Check that email 👀"); return; }
    if (password.length < 6) { setError("Password needs 6+ characters 🔒"); return; }
    setLoading(true);
    setError("");
    try {
      if (mode === "signup") {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(result.user, { displayName: name });
        onAuth(result.user.uid, name);
      } else {
        const result = await signInWithEmailAndPassword(auth, email, password);
        onAuth(result.user.uid, result.user.displayName || result.user.email || "Girl");
      }
    } catch (e: any) {
      if (e.code === "auth/email-already-in-use") setError("Email already registered — try logging in 💕");
      else if (e.code === "auth/user-not-found") setError("No account found — sign up instead 💕");
      else if (e.code === "auth/wrong-password") setError("Wrong password 🔒");
      else setError("Something went wrong. Try again 💕");
    }
    setLoading(false);
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "14px 16px",
    borderRadius: "14px",
    border: `1.5px solid ${CARD_BORDER}`,
    background: "#fff",
    color: "#1a1a1a",
    fontSize: "14px",
    outline: "none",
    fontFamily: "'Poppins',sans-serif",
    marginBottom: "10px",
    boxSizing: "border-box",
  };

  return (
    <div style={{ minHeight: "100vh", background: CREAM, display: "flex", flexDirection: "column" }}>
      <style>{FONTS + `@keyframes fadeUp { from { opacity:0;transform:translateY(16px) } to { opacity:1;transform:translateY(0) } }`}</style>

      {/* Top banner */}
      <div style={{ background: "linear-gradient(160deg,#FFF0F8,#FAF7F4)", padding: "52px 24px 32px", textAlign: "center", borderBottomLeftRadius: "32px", borderBottomRightRadius: "32px", borderBottom: `1px solid ${CARD_BORDER}` }}>
        <div style={{ width: "56px", height: "56px", borderRadius: "16px", background: GRAD, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px", boxShadow: "0 8px 24px rgba(255,105,180,0.3)" }}>
          <span style={{ fontSize: "20px", fontWeight: 900, color: "#fff", fontFamily: "'Poppins',sans-serif" }}>HGW</span>
        </div>
        <h1 style={{ fontFamily: "'Poppins',sans-serif", fontWeight: 900, fontSize: "36px", color: "#1a1a1a", letterSpacing: "-1.5px", margin: 0 }}>Hot Girl Walk</h1>
        <p style={{ fontFamily: "'Poppins',sans-serif", fontSize: "13px", color: "#999", marginTop: "4px" }}>london routes · iconic playlists</p>
      </div>

      <div style={{ flex: 1, padding: "28px 24px", maxWidth: "420px", margin: "0 auto", width: "100%", animation: "fadeUp 0.4s ease" }}>

        {/* Toggle */}
        <div style={{ display: "flex", background: "#F5F0F5", borderRadius: "50px", padding: "4px", marginBottom: "24px" }}>
          {(["signup", "login"] as const).map(m => (
            <button
              key={m}
              onClick={() => setMode(m)}
              style={{ flex: 1, padding: "10px", borderRadius: "50px", border: "none", cursor: "pointer", fontFamily: "'Poppins',sans-serif", fontWeight: 700, fontSize: "13px", transition: "all 0.25s ease", background: mode === m ? GRAD : "transparent", color: mode === m ? "#fff" : "#999", boxShadow: mode === m ? "0 4px 16px rgba(255,105,180,0.3)" : "none" }}>
              {m === "signup" ? "Sign Up" : "Log In"}
            </button>
          ))}
        </div>

        {/* Google */}
        <button
          onClick={handleGoogle}
          disabled={loading}
          style={{ width: "100%", padding: "14px", borderRadius: "14px", border: `1.5px solid ${CARD_BORDER}`, background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", fontFamily: "'Poppins',sans-serif", fontWeight: 600, fontSize: "14px", color: "#1a1a1a", marginBottom: "16px" }}>
          <svg width="18" height="18" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
          <div style={{ flex: 1, height: "1px", background: CARD_BORDER }} />
          <span style={{ fontFamily: "'Poppins',sans-serif", fontSize: "12px", color: "#bbb" }}>or</span>
          <div style={{ flex: 1, height: "1px", background: CARD_BORDER }} />
        </div>

        {/* Form */}
        {mode === "signup" && (
          <input style={inputStyle} placeholder="Your name 💕" value={name} onChange={e => setName(e.target.value)} />
        )}
        <input style={inputStyle} type="email" placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} />
        <input style={inputStyle} type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} />

        {error && (
          <p style={{ fontFamily: "'Poppins',sans-serif", fontSize: "13px", color: PINK, marginBottom: "12px", textAlign: "center" }}>{error}</p>
        )}

        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{ width: "100%", padding: "16px", borderRadius: "16px", border: "none", cursor: "pointer", background: GRAD, color: "#fff", fontSize: "14px", fontWeight: 700, fontFamily: "'Poppins',sans-serif", boxShadow: "0 8px 24px rgba(255,105,180,0.3)", marginBottom: "16px", opacity: loading ? 0.7 : 1 }}>
          {loading ? "Please wait..." : mode === "signup" ? "Create Account ✨" : "Log In 💕"}
        </button>

        <p style={{ fontFamily: "'Poppins',sans-serif", fontSize: "11px", color: "#bbb", textAlign: "center" }}>
          By continuing you agree to our Terms and Privacy Policy
        </p>
      </div>
    </div>
  );
}
